import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Sparkles } from 'lucide-react';

// Emotion types with colors
const EMOTIONS = [
  { name: 'joy', color: 'rgb(16, 185, 129)', emoji: '😊' },
  { name: 'sad', color: 'rgb(59, 130, 246)', emoji: '😢' },
  { name: 'anger', color: 'rgb(239, 68, 68)', emoji: '😠' },
  { name: 'fear', color: 'rgb(139, 92, 246)', emoji: '😨' },
  { name: 'surprise', color: 'rgb(245, 158, 11)', emoji: '😲' },
  { name: 'disgust', color: 'rgb(6, 182, 212)', emoji: '🤢' },
  { name: 'neutral', color: 'rgb(107, 114, 128)', emoji: '😐' }
];

const FEATURE_TYPES = [
  'facial expression', 'voice tone', 'text sentiment', 'skin conductance',
  'heart rate', 'blood pressure', 'attention span', 'response time',
  'energy level', 'stress level', 'social cues', 'micro expression',
  'behavioral pattern', 'confidence score', 'heart rhythm', 'ecg pulse'
];

// Generate random particle
const generateParticle = () => {
  const emotion = EMOTIONS[Math.floor(Math.random() * EMOTIONS.length)];
  const featureType = FEATURE_TYPES[Math.floor(Math.random() * FEATURE_TYPES.length)];
  const isECG = Math.random() < 0.2; // 20% chance of ECG type
  
  return {
    id: Math.random().toString(36).substr(2, 9),
    x: (Math.random() - 0.5) * 240,
    y: (Math.random() - 0.5) * 240,
    z: (Math.random() - 0.5) * 240,
    color: isECG ? 'rgb(255, 71, 87)' : emotion.color,
    size: 6 + Math.random() * 3,
    opacity: 0.1 + Math.random() * 0.5,
    type: isECG ? 'ECG Emotion Feature' : 'Emotion Feature',
    feature: featureType,
    scale: isECG ? 1 + Math.random() * 0.35 : 1,
    isECG: isECG
  };
};

// Generate large emotion sphere
const generateEmotionSphere = () => {
  const emotion = EMOTIONS[Math.floor(Math.random() * EMOTIONS.length)];
  const x = (Math.random() - 0.5) * 160; // Closer to center
  const y = (Math.random() - 0.5) * 160;
  const z = (Math.random() - 0.5) * 120;
  const confidence = 20 + Math.floor(Math.random() * 80); // 20-100%
  
  return {
    id: Math.random().toString(36).substr(2, 9),
    emotion: emotion.name,
    emoji: emotion.emoji,
    color: emotion.color,
    x,
    y,
    z,
    confidence,
    scale: 1 + (confidence / 200) // Larger spheres for higher confidence
  };
};

// Generate trail particles
const generateTrailParticles = (count = 50) => {
  return Array.from({ length: count }, () => {
    const progress = Math.random();
    return {
      id: Math.random().toString(36).substr(2, 9),
      x: (Math.random() - 0.5) * 150,
      y: (Math.random() - 0.5) * 150,
      z: (Math.random() - 0.5) * 150,
      size: 4 + Math.random() * 3,
      opacity: -0.1 + Math.random() * 0.4, // Some negative for fade out
      scale: 0.7 + Math.random() * 0.6,
      progress
    };
  });
};

const EmotionCube3D = () => {
  const [particles, setParticles] = useState([]);
  const [emotionSpheres, setEmotionSpheres] = useState([]);
  const [trailParticles, setTrailParticles] = useState([]);
  const [rotation, setRotation] = useState({ x: 15, y: 137 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMouse, setLastMouse] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Generate initial particles only (no spheres initially)
  useEffect(() => {
    const initialParticles = Array.from({ length: 120 }, () => generateParticle());
    const initialTrails = generateTrailParticles(50);
    setParticles(initialParticles);
    setTrailParticles(initialTrails);
    // Don't generate spheres initially - they appear only on button click
  }, []);

  // Auto-rotation animation
  useEffect(() => {
    if (!isDragging) {
      const interval = setInterval(() => {
        setRotation(prev => ({
          ...prev,
          y: (prev.y + 0.5) % 360
        }));
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isDragging]);

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastMouse({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - lastMouse.x;
      const deltaY = e.clientY - lastMouse.y;
      
      setRotation(prev => ({
        x: Math.max(-90, Math.min(90, prev.x + deltaY * 0.5)),
        y: (prev.y + deltaX * 0.5) % 360
      }));
      
      setLastMouse({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Generate new sample emotions (random)
  const generateSampleEmotions = () => {
    const newParticles = Array.from({ length: 120 }, () => generateParticle());
    const newSpheres = Array.from({ length: 3 }, () => generateEmotionSphere());
    const newTrails = generateTrailParticles(50);
    setParticles(newParticles);
    setEmotionSpheres(newSpheres);
    setTrailParticles(newTrails);
  };

  // Generate spheres from actual emotion analysis results
  const generateEmotionsFromAnalysis = (emotionResults) => {
    // emotionResults format: 
    // Array: [{ emotion: 'joy', confidence: 85 }, ...]
    // Single: { emotion: 'joy', confidence: 85 }
    // Object: { Joy: 0.85, Sad: 0.15, ... }
    
    const newParticles = Array.from({ length: 120 }, () => generateParticle());
    const newTrails = generateTrailParticles(50);
    
    let spheres = [];
    
    // Check if it's an array of emotions (from our analysis)
    if (Array.isArray(emotionResults)) {
      emotionResults.forEach(({ emotion, confidence }) => {
        const emotionData = EMOTIONS.find(e => e.name.toLowerCase() === emotion.toLowerCase());
        if (emotionData) {
          const x = (Math.random() - 0.5) * 160;
          const y = (Math.random() - 0.5) * 160;
          const z = (Math.random() - 0.5) * 120;
          
          spheres.push({
            id: Math.random().toString(36).substr(2, 9),
            emotion: emotionData.name,
            emoji: emotionData.emoji,
            color: emotionData.color,
            x, y, z,
            confidence: confidence,
            scale: 1 + (confidence / 200)
          });
        }
      });
    }
    // Check if it's a single emotion result
    else if (emotionResults.emotion && emotionResults.confidence) {
      const emotionData = EMOTIONS.find(e => e.name.toLowerCase() === emotionResults.emotion.toLowerCase());
      if (emotionData) {
        const x = (Math.random() - 0.5) * 160;
        const y = (Math.random() - 0.5) * 160;
        const z = (Math.random() - 0.5) * 120;
        
        spheres.push({
          id: Math.random().toString(36).substr(2, 9),
          emotion: emotionData.name,
          emoji: emotionData.emoji,
          color: emotionData.color,
          x, y, z,
          confidence: emotionResults.confidence,
          scale: 1 + (emotionResults.confidence / 200)
        });
      }
    } 
    // Check if it's multiple emotions (object with emotion scores)
    else if (typeof emotionResults === 'object') {
      const sortedEmotions = Object.entries(emotionResults)
        .filter(([key, value]) => typeof value === 'number')
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3); // Top 3 emotions
      
      sortedEmotions.forEach(([emotionName, confidence]) => {
        const emotionData = EMOTIONS.find(e => 
          e.name.toLowerCase() === emotionName.toLowerCase() ||
          emotionName.toLowerCase().includes(e.name.toLowerCase())
        );
        
        if (emotionData) {
          const x = (Math.random() - 0.5) * 160;
          const y = (Math.random() - 0.5) * 160;
          const z = (Math.random() - 0.5) * 120;
          const confidencePercent = confidence > 1 ? confidence : confidence * 100;
          
          spheres.push({
            id: Math.random().toString(36).substr(2, 9),
            emotion: emotionData.name,
            emoji: emotionData.emoji,
            color: emotionData.color,
            x, y, z,
            confidence: Math.round(confidencePercent),
            scale: 1 + (confidencePercent / 200)
          });
        }
      });
    }
    
    // Only set spheres if we actually generated some
    // Don't generate random ones as fallback
    setParticles(newParticles);
    setEmotionSpheres(spheres); // Will be empty array if no match
    setTrailParticles(newTrails);
  };

  // Expose function for external use
  useEffect(() => {
    window.updateEmotionCube = generateEmotionsFromAnalysis;
    return () => {
      delete window.updateEmotionCube;
    };
  }, []);

  return (
    <div className="max-w-6xl mx-auto space-y-6 px-4">
      <div className="bg-[#C8DDD3] rounded-3xl p-4 sm:p-6">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
          3D Emotion Cube Visualization
        </h3>
        <p className="text-sm sm:text-base text-gray-700 font-light mb-4 sm:mb-6">
          Explore emotional data plotted in a scientific 3D coordinate system with geometric boundaries and real-time interaction.
        </p>
        
        {/* 3D Cube Container */}
        <div 
          ref={containerRef}
          className="h-64 sm:h-80 md:h-96 relative bg-black rounded-lg overflow-hidden cursor-move select-none touch-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY });
          }}
          onTouchMove={(e) => {
            const touch = e.touches[0];
            handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
          }}
          onTouchEnd={handleMouseUp}
        >
          {/* 3D Scene */}
          <div 
            className="relative w-full h-full flex items-center justify-center"
            style={{ perspective: '1200px' }}
          >
            <div
              className="relative"
              style={{
                transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
                transformStyle: 'preserve-3d',
                width: '400px',
                height: '400px',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              {/* Cube Faces */}
              {/* Front and Back (cyan) */}
              <div className="absolute border-2 border-cyan-400 opacity-60" style={{
                width: '240px', height: '240px',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%) translateZ(120px)',
                transformStyle: 'preserve-3d'
              }} />
              <div className="absolute border-2 border-cyan-400 opacity-40" style={{
                width: '240px', height: '240px',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%) translateZ(-120px)',
                transformStyle: 'preserve-3d'
              }} />
              
              {/* Left and Right (purple) */}
              <div className="absolute border-2 border-purple-400 opacity-50" style={{
                width: '240px', height: '240px',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%) rotateY(90deg) translateZ(120px)',
                transformStyle: 'preserve-3d'
              }} />
              <div className="absolute border-2 border-purple-400 opacity-30" style={{
                width: '240px', height: '240px',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%) rotateY(90deg) translateZ(-120px)',
                transformStyle: 'preserve-3d'
              }} />
              
              {/* Top and Bottom (green) */}
              <div className="absolute border-2 border-green-400 opacity-50" style={{
                width: '240px', height: '240px',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%) rotateX(90deg) translateZ(120px)',
                transformStyle: 'preserve-3d'
              }} />
              <div className="absolute border-2 border-green-400 opacity-30" style={{
                width: '240px', height: '240px',
                left: '50%', top: '50%',
                transform: 'translate(-50%, -50%) rotateX(90deg) translateZ(-120px)',
                transformStyle: 'preserve-3d'
              }} />

              {/* Corner Points */}
              {[
                [-120, -120, -120], [120, -120, -120],
                [-120, 120, -120], [120, 120, -120],
                [-120, -120, 120], [120, -120, 120],
                [-120, 120, 120], [120, 120, 120]
              ].map((pos, i) => (
                <div
                  key={`corner-${i}`}
                  className="absolute w-2 h-2 bg-white rounded-full opacity-60"
                  style={{
                    left: '50%',
                    top: '50%',
                    transform: `translate3d(${pos[0]}px, ${pos[1]}px, ${pos[2]}px) translate(-50%, -50%)`,
                    transformStyle: 'preserve-3d',
                    boxShadow: '0 0 8px rgba(255, 255, 255, 0.6)'
                  }}
                />
              ))}

              {/* Particles */}
              {particles.map((particle) => (
                <div
                  key={particle.id}
                  className="absolute rounded-full transform-gpu"
                  title={`${particle.type}: ${particle.feature}`}
                  style={{
                    width: `${particle.size}px`,
                    height: `${particle.size}px`,
                    left: '50%',
                    top: '50%',
                    transform: `translate3d(${particle.x}px, ${particle.y}px, ${particle.z}px) translate(-50%, -50%) scale(${particle.scale})`,
                    backgroundColor: particle.color,
                    opacity: particle.opacity,
                    transformStyle: 'preserve-3d',
                    boxShadow: particle.isECG 
                      ? `0 0 ${particle.size * 3}px ${particle.color.replace(')', ', 0.5)').replace('rgb', 'rgba')}, 0 0 ${particle.size * 6}px ${particle.color.replace(')', ', 0.25)').replace('rgb', 'rgba')}`
                      : `0 0 ${particle.size * 2}px ${particle.color.replace(')', ', 0.25)').replace('rgb', 'rgba')}`,
                    transition: particle.isECG ? 'none' : 'opacity 0.1s ease-out',
                    border: particle.isECG ? `1px solid ${particle.color}` : 'none'
                  }}
                />
              ))}

              {/* Trail Particles */}
              {trailParticles.map((trail) => (
                <div
                  key={trail.id}
                  className="absolute rounded-full transform-gpu"
                  style={{
                    width: `${trail.size}px`,
                    height: `${trail.size}px`,
                    left: '50%',
                    top: '50%',
                    transform: `translate3d(${trail.x}px, ${trail.y}px, ${trail.z}px) translate(-50%, -50%) scale(${trail.scale})`,
                    backgroundColor: 'rgb(0, 255, 136)',
                    opacity: trail.opacity,
                    transformStyle: 'preserve-3d',
                    boxShadow: '0 0 10px rgba(0, 255, 136, 0.376)'
                  }}
                />
              ))}

              {/* Large Emotion Spheres */}
              {emotionSpheres.map((sphere) => {
                const distance = Math.sqrt(sphere.x ** 2 + sphere.y ** 2 + sphere.z ** 2);
                const angleX = Math.atan2(sphere.y, sphere.z) * (180 / Math.PI);
                const angleZ = Math.atan2(sphere.x, Math.sqrt(sphere.y ** 2 + sphere.z ** 2)) * (180 / Math.PI);
                const colorOpacity = sphere.color.replace(')', ', 0.376)').replace('rgb', 'rgba');
                
                return (
                  <div
                    key={sphere.id}
                    className="absolute transform-gpu"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `translate3d(${sphere.x}px, ${sphere.y}px, ${sphere.z}px) translate(-50%, -50%)`,
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    {/* Main Sphere */}
                    <div
                      className="w-12 h-12 rounded-full relative flex items-center justify-center text-white font-bold text-sm cursor-pointer transform-gpu"
                      title={`${sphere.emotion}: ${sphere.confidence}%`}
                      style={{
                        background: `radial-gradient(circle at 30% 30%, ${sphere.color} 0%, ${sphere.color.replace(')', ', 0.8)').replace('rgb', 'rgba')} 35%, ${sphere.color.replace(')', ', 0.533)').replace('rgb', 'rgba')} 70%, ${sphere.color.replace(')', ', 0.267)').replace('rgb', 'rgba')} 100%)`,
                        boxShadow: `${colorOpacity.replace('0.376', '0.44')} 0 0 25px, ${colorOpacity.replace('0.376', '0.25')} 0 0 50px, ${colorOpacity.replace('0.376', '0.19')} -8px -8px 16px inset, rgba(255, 255, 255, 0.3) 8px 8px 16px inset, rgba(0, 0, 0, 0.3) 0 8px 25px`,
                        border: `1px solid ${sphere.color.replace(')', ', 0.667)').replace('rgb', 'rgba')}`,
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {/* Highlight */}
                      <div 
                        className="absolute top-2 left-3 w-3 h-3 rounded-full opacity-60"
                        style={{
                          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.2) 70%, transparent 100%)',
                          filter: 'blur(0.5px)'
                        }}
                      />
                      
                      {/* Emoji */}
                      <div className="relative z-10 filter drop-shadow-sm">
                        {sphere.emoji}
                      </div>
                      
                      {/* Ping Border */}
                      <div 
                        className="absolute inset-0 rounded-full border-2 border-white opacity-40 animate-ping"
                        style={{
                          transform: `scale(${sphere.scale})`,
                          boxShadow: 'rgba(255, 255, 255, 0.5) 0 0 15px, rgba(255, 255, 255, 0.2) 0 0 10px inset',
                          animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite'
                        }}
                      />
                      
                      {/* Shadow */}
                      <div 
                        className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-2 rounded-full opacity-30"
                        style={{
                          background: `radial-gradient(${colorOpacity} 0%, transparent 70%)`,
                          filter: 'blur(2px)'
                        }}
                      />
                    </div>

                    {/* Connection Line to Center */}
                    <div 
                      className="absolute top-1/2 left-1/2 opacity-40"
                      style={{
                        width: '2px',
                        height: `${distance}px`,
                        background: `linear-gradient(${colorOpacity.replace('0.376', '0.5')}, transparent)`,
                        transform: `translate(-50%, -50%) rotateX(${angleX}deg) rotateZ(${angleZ}deg)`,
                        transformOrigin: '50% 0%'
                      }}
                    />

                    {/* Tooltip */}
                    <div 
                      className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white px-2 py-1 rounded text-xs whitespace-nowrap backdrop-blur-sm"
                      style={{
                        transform: `translateX(-50%) rotateY(${-rotation.y}deg) rotateX(${-rotation.x}deg)`,
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {sphere.emotion}<br />
                      <span className="text-gray-300">{sphere.confidence}%</span>
                    </div>
                  </div>
                );
              })}

              {/* Grid Reference Lines */}
              {[-60, 0, 60].map((offset) => (
                <React.Fragment key={`grid-${offset}`}>
                  <div className="absolute border border-gray-600 opacity-15" style={{
                    width: '240px', height: '240px',
                    left: '50%', top: '50%',
                    transform: `translate(-50%, -50%) translateX(${offset}px)`,
                    transformStyle: 'preserve-3d'
                  }} />
                  <div className="absolute border border-gray-600 opacity-15" style={{
                    width: '240px', height: '240px',
                    left: '50%', top: '50%',
                    transform: `translate(-50%, -50%) translateY(${offset}px)`,
                    transformStyle: 'preserve-3d'
                  }} />
                  <div className="absolute border border-gray-600 opacity-15" style={{
                    width: '240px', height: '240px',
                    left: '50%', top: '50%',
                    transform: `translate(-50%, -50%) translateZ(${offset}px)`,
                    transformStyle: 'preserve-3d'
                  }} />
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Left Panel - Cube Info (hidden on small screens) */}
          <div className="hidden lg:block absolute top-4 left-4 bg-black bg-opacity-90 text-white p-4 rounded-lg text-xs backdrop-blur-sm border border-gray-700 max-w-xs">
            <h4 className="font-bold mb-3 text-cyan-400">3D Emotion Data Cube</h4>
            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 border border-cyan-400"></div>
                <span>Cube Boundaries</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span>Corner Points</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-1 bg-gray-600"></div>
                <span>Grid Reference</span>
              </div>
            </div>
            
            <div className="mb-3 pt-3 border-t border-gray-600">
              <h5 className="font-semibold mb-2 text-green-400">Data Features</h5>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                  <span>Blinking: Live Data</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-ping"></div>
                  <span>Pulsing: Processing</span>
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-600">
              <h5 className="font-semibold mb-2 text-yellow-400">Emotions</h5>
              <div className="grid grid-cols-2 gap-1">
                {EMOTIONS.slice(0, 6).map((emotion) => (
                  <div key={emotion.name} className="flex items-center gap-1">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: emotion.color }}
                    />
                    <span>{emotion.emoji} {emotion.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Controls (hidden on small screens) */}
          <div className="hidden lg:block absolute top-4 right-4 bg-black bg-opacity-90 text-white p-4 rounded-lg text-xs backdrop-blur-sm border border-gray-700">
            <h4 className="font-bold mb-3 text-purple-400">Controls</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-green-400">🖱️</span>
                <span>Mouse: Rotate View</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-400">⚙️</span>
                <span>Auto: {isDragging ? 'OFF' : 'ON'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-yellow-400">📊</span>
                <span>Live Data Points</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-red-400">🎯</span>
                <span>Confidence Mapped</span>
              </div>
            </div>
          </div>

          {/* Bottom Right - Current Data Points (responsive) */}
          <div className="hidden sm:block absolute bottom-4 right-4 bg-black bg-opacity-90 text-white p-2 sm:p-3 rounded-lg text-xs backdrop-blur-sm border border-gray-700">
            <h5 className="font-semibold mb-2 text-cyan-400">Current Data Points</h5>
            {emotionSpheres.map((sphere, idx) => (
              <div key={sphere.id} className="flex items-center gap-2 mb-1">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: sphere.color }}
                />
                <span className="capitalize">{sphere.emotion}:</span>
                <span className="text-gray-300">
                  ({Math.round(sphere.x)}, {Math.round(sphere.y)}, {Math.round(sphere.z)})
                </span>
              </div>
            ))}
          </div>

          {/* Bottom Text (responsive) */}
          <div className="absolute bottom-1 sm:bottom-4 left-1/2 transform -translate-x-1/2 text-white text-center px-2 w-full">
            <h2 className="text-xs sm:text-base md:text-xl font-bold text-white">
              3D Emotion Cube
            </h2>
            <p className="hidden sm:block text-xs sm:text-sm text-gray-400 mt-1">
              Geometric Visualization
            </p>
            <div className="hidden md:flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
              <span>🔷 Cube Boundaries</span>
              <span>📊 Live Data Points</span>
              <span>🎯 Confidence Mapped</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default EmotionCube3D;
